import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCrm, isClosedStage } from '../lib/crm';
import { useCurrentUser } from '../lib/currentUser';
import { useNavCounts } from '../lib/hooks/useNavCounts';
import { useTheme } from '../lib/theme';
import { NAV_ICONS } from './NavIcons';
import { appForPath, defaultPathForApp, rolesForPath, visibleApps, visibleItems } from '../lib/apps';
import { allowedByRoles, roleLabel } from '../lib/roles';
import { useOrgName } from '../lib/useOrgName';

// Theme-aware brand logos (tightly-cropped, in /public).
const LOGO_DARK  = '/logo-dark (2).png';
const LOGO_LIGHT = '/logo-light (2).png';

const STORAGE_KEY = 'spectraiq.nav.collapsed';
const LAST_PATH_KEY = 'spectraiq.nav.lastPath';

/**
 * Nav key -> the `data-tour` hook the product tour spotlights (lib/tour.jsx).
 * Kept as a map rather than derived from the key so renaming a nav item can't
 * silently break the tour — an unmatched selector just skips that step. Steps
 * whose target is in another app are skipped automatically by the tour.
 */
const TOUR_ANCHORS = {
  leads:    'nav-leads',
  dead:     'nav-dead-leads',
  triage:   'nav-triage',
  settings: 'nav-settings',
  help:     'nav-help',
};

/* ── last-visited path per app, so switching back returns where you were ── */
function readLastPaths() {
  try {
    const raw = localStorage.getItem(LAST_PATH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) { return {}; }
}
function writeLastPath(appKey, path) {
  try {
    const map = readLastPaths();
    map[appKey] = path;
    localStorage.setItem(LAST_PATH_KEY, JSON.stringify(map));
  } catch (_) { /* private mode */ }
}

/* ── App switcher: compact popover listing the five apps ── */
function AppSwitcher({ currentApp, collapsed, roles }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const switchTo = (app) => {
    setOpen(false);
    if (app.key === currentApp.key) return;
    const remembered = readLastPaths()[app.key];
    // Only trust a remembered path that still belongs to that app AND that this
    // user may open — the memory can predate a role change, or another person's
    // session on the same browser.
    const ok = remembered
      && appForPath(remembered).key === app.key
      && allowedByRoles(roles, rolesForPath(remembered));
    navigate(ok ? remembered : defaultPathForApp(app.key, roles));
  };

  const CurrentIcon = NAV_ICONS[currentApp.icon];

  return (
    <div ref={ref} className="app-switcher" data-tour="app-switcher">
      <button
        type="button"
        className="app-switcher-btn"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? `${currentApp.label} — switch app` : 'Switch app'}
      >
        <span className="app-switcher-glyph" style={{ color: `var(${currentApp.accentVar})` }}>
          {CurrentIcon ? <CurrentIcon size={16} /> : null}
        </span>
        <span className="sidebar-only-expanded app-switcher-current">
          <span className="app-switcher-name">{currentApp.label}</span>
          <span className="app-switcher-tagline">{currentApp.tagline}</span>
        </span>
        <span className="sidebar-only-expanded app-switcher-caret" aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div role="menu" className="app-switcher-menu">
          <div className="app-switcher-menu-label mono">SWITCH APP</div>
          {visibleApps(roles).map(app => {
            const Icon = NAV_ICONS[app.icon];
            const active = app.key === currentApp.key;
            return (
              <button
                key={app.key}
                type="button"
                role="menuitem"
                className={`app-switcher-item ${active ? 'active' : ''}`}
                onClick={() => switchTo(app)}
              >
                <span className="app-switcher-glyph" style={{ color: `var(${app.accentVar})` }}>
                  {Icon ? <Icon size={16} /> : null}
                </span>
                <span className="app-switcher-item-text">
                  <span className="app-switcher-name">{app.label}</span>
                  <span className="app-switcher-tagline">{app.tagline}</span>
                </span>
                {active && <span className="app-switcher-check" aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * What each nav item is allowed to badge, and what the number means.
 *
 * One rule: a badge is WORK WAITING — something a person can go and finish,
 * so the number can reach zero. Counts that describe a state of the world
 * rather than a queue of work (how many leads are scoring well, how many
 * customers exist) belong in the hover text or on the dashboard, not here.
 *
 * Every badge also carries a `title`, because a bare digit in a nav is a
 * riddle. The reason this codebase had a `3` nobody could explain is that
 * nothing on screen ever said what it counted.
 *
 * Tone is meaning, not emphasis: danger = someone is waiting on us,
 * warning = incomplete but nobody is blocked, accent = neutral information.
 */
function badgeFor(item, counts, hotLeads) {
  switch (item.key) {
    case 'leads': {
      const hotNote = hotLeads > 0 ? ` · ${hotLeads} hot (85+ or negotiating)` : '';
      if (counts.needsAttention == null) {
        // Counts unavailable — say nothing numeric, but the hover text can
        // still carry what we do know from the cached lead list.
        return hotNote ? { value: null, title: `${hotLeads} hot leads` } : null;
      }
      if (counts.needsAttention === 0) {
        return { value: null, title: `Nothing needs attention${hotNote}` };
      }
      return {
        // Distinct flagged leads — the same count the board's "Needs attention"
        // filter shows, so the badge and the page always agree.
        value: counts.needsAttention,
        tone: counts.unactioned > 0 ? 'danger' : 'warning',
        title: `${plural(counts.needsAttention, 'lead')} need attention${hotNote} — click the number to open them`,
        // Land on exactly the leads the badge counts, not the unfiltered board.
        to: '/leads?filter=attention',
      };
    }
    case 'tasks':
      if (!counts.overdueTasks) return null;
      return {
        value: counts.overdueTasks,
        tone: 'danger',
        title: `${plural(counts.overdueTasks, 'task')} overdue`,
      };
    case 'appointments':
      if (!counts.appointmentsToday) return null;
      return {
        value: counts.appointmentsToday,
        tone: 'accent',
        title: `${plural(counts.appointmentsToday, 'appointment')} today`,
      };
    default:
      // Anything still carrying a literal in lib/apps.js. There are none left;
      // this is here so a future literal is at least rendered consistently
      // rather than silently taking a different code path.
      return item.badge ? { value: item.badge, tone: 'danger', title: undefined } : null;
  }
}

export default function SideNav() {
  const navigate = useNavigate();
  const { leads } = useCrm();
  const { role, roles, consultantId } = useCurrentUser();
  // "Sales Manager · Bayside Motors" — the signed-in role and dealership, never a
  // hard-coded label (which showed one fixed role and dealer name to everyone).
  const orgName = useOrgName();
  const identityLabel = [roles?.length ? roleLabel(role) : null, orgName].filter(Boolean).join(' · ');

  /**
   * Re-fetch the nav counts whenever any lead's STAGE changes.
   *
   * The hook polls on a slow cadence because the nav is ambient, not a live
   * view. But "slow" is the wrong answer for a change the user just made
   * themselves: mark a lead lost, look left, see the same number, conclude the
   * badge is decorative. CrmProvider already polls leads every 15s and pages
   * call refreshLeads() straight after a mutation, so watching this signature
   * makes the nav follow the user's own action almost immediately.
   *
   * Deliberately stage-only. The leads array is replaced on every poll, so
   * depending on the array itself would refetch every 15 seconds forever; and
   * a change to a lead's notes or score cannot move an exception count.
   */
  const stageSignature = useMemo(
    () => leads.map((l) => `${l.id}:${l.stage}`).join('|'),
    [leads],
  );

  const counts = useNavCounts({ role, consultantId, watch: stageSignature });

  /**
   * "Hot" is kept, but demoted out of the badge and into the tooltip.
   *
   * It is a real and interesting number — open leads scoring 85+ or already
   * negotiating — but it is an OPPORTUNITY, and an opportunity count has no
   * completion state. Nothing a user does makes it go down, so as a badge it
   * sat at the same value every day and stopped being read. Checked against
   * live data: it is 3, it is genuinely 3, and it has been 3 the whole time.
   *
   * The badge now carries work instead — see NAV_BADGES — and this rides along
   * in the hover text, where a number you cannot act on belongs.
   *
   * Closed leads are excluded, which was a real latent bug in the old filter
   * (a lead scoring 90 that was later marked LOST stayed "hot" forever) even
   * though it is not what produced the 3. DELIVERY_BOOKED still counts here,
   * which is arguably wrong for the same reason CONFIRMED was dropped — a sold
   * car awaiting handover is not a lead to accelerate. Left alone pending a
   * product call rather than changed quietly.
   */
  const hotLeads = leads.filter(
    l => !isClosedStage(l.stage) && (l.score >= 85 || l.stage === 'NEGOTIATION'),
  ).length;

  const location = useLocation();
  const currentApp = appForPath(location.pathname);

  // Remember where the user was inside each app so the switcher can return there.
  useEffect(() => {
    writeLastPath(currentApp.key, location.pathname);
  }, [currentApp.key, location.pathname]);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch (_) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch (_) {}
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('nav-collapsed', collapsed);
    }
  }, [collapsed]);

  const { theme, mode } = useTheme();
  // Pick the logo for the SIDEBAR's background, not the app mode: the
  // office365 theme uses a dark (Fluent) sidebar in both modes, so it
  // always needs the dark-background logo.
const ALWAYS_DARK_SIDEBAR_THEMES = ['office365', 'standard'];
const sidebarIsDark = ALWAYS_DARK_SIDEBAR_THEMES.includes(theme) || mode === 'dark';
const logoSrc = sidebarIsDark ? LOGO_DARK : LOGO_LIGHT;

  const toggle = useCallback(() => setCollapsed(v => !v), []);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`} aria-label="Primary navigation">
      <div className="sidebar-logo">
        {collapsed ? (
          <div className="sidebar-mark" style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'var(--accent-gradient)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17, color: '#fff', fontWeight: 800, flexShrink: 0,
          }}>S</div>
        ) : (
          <img src={logoSrc} alt="SpectraIQ" className="sidebar-logo-img"
               style={{ display: 'block', height: 104, width: 'auto', maxWidth: '100%', objectFit: 'contain', margin: '0 auto' }} />
        )}
      </div>

      <AppSwitcher currentApp={currentApp} collapsed={collapsed} roles={roles} />

      {identityLabel && <div className="sidebar-label sidebar-only-expanded">{identityLabel}</div>}

      <nav className="sidebar-nav">
        {visibleItems(currentApp, roles).map(item => {
          const Icon = NAV_ICONS[item.key];
          const badge = badgeFor(item, counts, hotLeads);
          return (
            <NavLink
              key={item.key}
              to={item?.path || '/'}
              end={item?.path === '/'}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              title={collapsed ? item.label : badge?.title}
              data-tour={TOUR_ANCHORS[item.key]}
            >
              <span className="sidebar-item-inner">
                <span className="sidebar-icon">{Icon ? <Icon size={16} /> : null}</span>
                <span className="sidebar-label-text sidebar-only-expanded">{item.label}</span>
              </span>
              {/* `badge.value == null` is a deliberate state: hover text with
                  no pill, for an item whose counts are known to be zero or
                  unavailable. A zero badge is clutter; the tooltip still
                  answers "is there anything here?" if someone asks. */}
              {badge?.value != null && (
                <>
                  {/* The menu item itself always opens the unfiltered page. Only a
                      click on the number opens the filtered view (when it has one). */}
                  <span
                    className={`sidebar-badge ${badge.tone} sidebar-only-expanded`}
                    onClick={badge.to ? (e) => { e.preventDefault(); e.stopPropagation(); navigate(badge.to); } : undefined}
                    style={badge.to ? { cursor: 'pointer' } : undefined}
                  >
                    {badge.value > 99 ? '99+' : badge.value}
                  </span>
                  <span className={`sidebar-badge-dot sidebar-badge-dot-${badge.tone} sidebar-only-collapsed`} aria-hidden="true" />
                </>
              )}
            </NavLink>
          );
        })}
      </nav>


      <button
        type="button"
        className="sidebar-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={`${collapsed ? 'Expand' : 'Collapse'} sidebar (Ctrl+B)`}
      >
        <span className="sidebar-toggle-icon" aria-hidden="true">
          {collapsed ? '›' : '‹'}
        </span>
        <span className="sidebar-toggle-label sidebar-only-expanded">Collapse</span>
        <span className="sidebar-toggle-kbd sidebar-only-expanded mono">Ctrl B</span>
      </button>
    </aside>
  );
}
